import React, { useEffect, useMemo, useState, Component } from 'react';
import { Menu, X, AlertCircle } from 'lucide-react';
import { TeacherSidebarV2, TeacherV2Route } from './TeacherSidebarV2';
import { TodayPage } from './pages/TodayPage';
import { MyClassPage } from './pages/MyClassPage';
import { HomeworkPage } from './pages/HomeworkPage';
import { SubjectsPage } from './pages/SubjectsPage';
import { AssessmentsPage } from './pages/AssessmentsPage';
import { ReportsPage } from './pages/ReportsPage';
import { ManagementPage } from './pages/ManagementPage';
import { SettingsPage } from './pages/SettingsPage';
import { StudentDetailPanel, StudentData } from './pages/StudentDetailPanel';
import { useAppStore } from '../../app/hooks/useAppStore';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
import { SkeletonPage } from '../../components/shared/Skeleton';
import { isTeacherAccount, getLinkedLearners, getTeacherClassrooms, getClassroomRole } from '../../accounts';
import type { Account, Classroom } from '../../accounts';
import { warn } from '../../utils/logger';
import { GroupId } from '../../config/systemConfig';
import { ModuleId, MODULES } from '../../content';
import { getModuleMasteryFromFamilies, V11_CONFIG } from '../../mastery';
interface TeacherPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  onSelectStudent: (studentId: string) => void;
  activeAccount: Account;
  classrooms: Classroom[];
  selectedClassroomId: string | null;
  onSelectClassroom: (id: string | null) => void;
  onNavigate: (route: TeacherV2Route) => void;
  classroomName?: string;
  onViewDashboard?: (studentId: string) => void;
  initialTab?: 'klaarzetten' | 'resultaten';
  initialSearchQuery?: string;
  onViewAssessmentResults?: () => void;
  activeTeacherId?: string;
}
const PAGE_MAP: Record<TeacherV2Route, ComponentType<Partial<TeacherPageProps>>> = {
  today: TodayPage,
  class: MyClassPage,
  homework: HomeworkPage as ComponentType<Partial<TeacherPageProps>>,
  subjects: SubjectsPage,
  assessments: AssessmentsPage,
  reports: ReportsPage,
  management: ManagementPage,
  settings: SettingsPage as ComponentType<Partial<TeacherPageProps>>
};
const ROUTE_LABELS: Record<TeacherV2Route, string> = {
  today: 'Vandaag',
  class: 'Mijn Klas',
  homework: 'Huiswerk',
  subjects: 'Vakken',
  assessments: 'Toetsen',
  reports: 'Rapporten',
  management: 'Klas Beheer',
  settings: 'Instellingen'
};
interface TeacherShellV2Props {
  onNavigateToLearner?: (learnerId: string) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
}
export function TeacherShellV2({
  onNavigateToLearner,
  onLogout,
  onReturnHome
}: TeacherShellV2Props) {
  const [activeRoute, setActiveRoute] = useState<TeacherV2Route>('today');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [insightStudentId, setInsightStudentId] = useState<string | null>(null);
  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(null);
  const [assessmentInitialTab, setAssessmentInitialTab] = useState<'klaarzetten' | 'resultaten' | undefined>(undefined);
  const [assessmentSearchQuery, setAssessmentSearchQuery] = useState<string | undefined>(undefined);
  const {
    accounts,
    activeAccount,
    selectedGroup,
    getLearnerData,
    getLearnerModuleMastery,
    isInitialized,
    setViewingLearner,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner
  } = useAppStore();
  const isTeacher = activeAccount && isTeacherAccount(activeAccount);
  const activeClassroomRole = selectedClassroomId && activeAccount ? getClassroomRole(accounts, selectedClassroomId, activeAccount.id) : null;
  const isOwner = activeClassroomRole === 'owner';
  const classrooms = isTeacher ? getTeacherClassrooms(accounts, activeAccount.id) : [];
  useEffect(() => {
    if (selectedClassroomId && !classrooms.find(c => c.id === selectedClassroomId)) {
      setSelectedClassroomId(null);
    }
    // Auto-select the only classroom if none is selected
    if (!selectedClassroomId && classrooms.length === 1) {
      setSelectedClassroomId(classrooms[0].id);
    }
  }, [selectedClassroomId, classrooms]);
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const activeGroupLevel = (activeClassroom?.groupLevel || selectedGroup) as GroupId;
  const allLinkedLearners = isTeacher ? getLinkedLearners(accounts, activeAccount.id) : [];
  const displayLearners = activeClassroom ? allLinkedLearners.filter(l => activeClassroom.learnerIds.includes(l.id)) : allLinkedLearners;
  const students: StudentData[] = useMemo(() => {
    return displayLearners.map(learner => {
      const learnerData = getLearnerData(learner.id);
      if (!learnerData) {
        warn(`No data found for student ${learner.id}`);
        return null;
      }
      return {
        id: learner.id,
        name: learner.displayName,
        data: learnerData
      };
    }).filter((s): s is StudentData => s !== null);
  }, [displayLearners, getLearnerData]);
  const attentionCount = useMemo(() => {
    let count = 0;
    students.forEach(student => {
      let lowestMastery = 100;
      let hasPracticed = false;
      const data = getLearnerData(student.id);
      if (!data) return;
      MODULES.forEach(module => {
        const mastery = getModuleMasteryFromFamilies(data.familyMastery || {}, module.id, activeGroupLevel);
        if (mastery > 0) {
          hasPracticed = true;
          if (mastery < lowestMastery) lowestMastery = mastery;
        }
      });
      if (hasPracticed && lowestMastery < V11_CONFIG.STRUGGLING_THRESHOLD) count++;
    });
    return count;
  }, [students, getLearnerData, activeGroupLevel]);
  // Fix: use activeGroupLevel instead of global selectedGroup for mastery lookups
  // The global selectedGroup may not match the classroom's groupLevel, causing
  // mastery keys to look up the wrong group scope (e.g. _g6 instead of _g7)
  const getModuleMasteryForClassroom = useMemo(() => {
    return (learnerId: string, moduleId: ModuleId): number => {
      const data = getLearnerData(learnerId);
      if (!data) return 0;
      return getModuleMasteryFromFamilies(data.familyMastery || {}, moduleId, activeGroupLevel);
    };
  }, [getLearnerData, activeGroupLevel]);
  const studentIds = useMemo(() => students.map(s => s.id), [students]);
  useEffect(() => {
    if (insightStudentId && !studentIds.includes(insightStudentId)) {
      setInsightStudentId(null);
    }
  }, [insightStudentId, studentIds]);
  // Clear assessment navigation state when leaving assessments route
  useEffect(() => {
    if (activeRoute !== 'assessments') {
      setAssessmentInitialTab(undefined);
      setAssessmentSearchQuery(undefined);
    }
  }, [activeRoute]);
  if (!isInitialized) {
    return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-10387">
        <div className="hidden md:block w-64 bg-white border-r border-slate-200 shrink-0" data-id="element-10388" />
        <main className="flex-1 min-w-0 overflow-auto" data-id="element-10389">
          <SkeletonPage data-id="element-10390" />
        </main>
      </div>;
  }
  if (!isTeacher) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4" data-id="element-10391">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-sm border border-slate-200 text-center" data-id="element-10392">
          <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-10393">
            Geen toegang
          </h2>
          <p className="text-slate-600 mb-8" data-id="element-10394">
            Je bent niet ingelogd als docent. Gebruik de account switcher om
            naar een docentaccount te wisselen.
          </p>
          {onReturnHome && <button onClick={onReturnHome} className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-10395">
              Naar leerlingomgeving
            </button>}
        </div>
      </div>;
  }
  const handleSelectStudent = (studentId: string) => {
    setInsightStudentId(studentId);
  };
  const handleCloseInsight = () => {
    setInsightStudentId(null);
  };
  const handleViewDashboard = (studentId: string) => {
    setViewingLearner(studentId);
    if (onNavigateToLearner) {
      onNavigateToLearner(studentId);
    }
  };
  const handleNavigateToAssessments = (studentName: string) => {
    setAssessmentInitialTab('resultaten');
    setAssessmentSearchQuery(studentName);
    setActiveRoute('assessments');
    setInsightStudentId(null);
  };
  const handleViewAssessmentResults = () => {
    setAssessmentInitialTab('resultaten');
    setAssessmentSearchQuery(undefined);
    setActiveRoute('assessments');
  };
  const handleRemoveFromClassroom = (studentId: string) => {
    if (activeAccount && selectedClassroomId && isOwner) {
      removeLearnerFromClassroom(activeAccount.id, selectedClassroomId, studentId);
      handleCloseInsight();
    }
  };
  const handleUnlinkStudent = (studentId: string) => {
    if (activeAccount && isOwner) {
      unlinkTeacherFromLearner(activeAccount.id, studentId);
      handleCloseInsight();
    }
  };
  const insightStudent = insightStudentId ? students.find(s => s.id === insightStudentId) : null;
  const PageComponent = PAGE_MAP[activeRoute] ?? TodayPage;
  return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-10396">
      {/* Skip to content — a11y */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-10397">
        Ga naar inhoud
      </a>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-slate-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-10398">
        <div className="font-bold text-slate-900 flex items-center gap-3 py-3" data-id="element-10399">
          <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center border border-indigo-100" data-id="element-10400">
            <span className="text-indigo-600 font-black text-sm" data-id="element-10401">M</span>
          </div>
          <div className="flex flex-col" data-id="element-10402">
            <span className="text-sm font-black leading-tight" data-id="element-10403">
              {ROUTE_LABELS[activeRoute]}
            </span>
            {activeClassroom && <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-tight" data-id="element-10404">
                {activeClassroom.name}
              </span>}
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 -mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-10405">
          {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-10406" /> : <Menu className="w-6 h-6" data-id="element-10407" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-10408" />

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform shadow-xl md:shadow-none
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-10409">
        <TeacherSidebarV2 activeRoute={activeRoute} onNavigate={route => {
        setActiveRoute(route);
        setMobileMenuOpen(false);
      }} onLogout={onLogout} onReturnHome={onReturnHome} classroomName={activeClassroom?.name || 'Geen klas'} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={setSelectedClassroomId} todayAttentionCount={attentionCount} activeTeacherId={activeAccount.id} data-id="element-10410" />
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 overflow-auto pt-16 md:pt-0 relative" data-id="element-10411">
        <BaseErrorBoundary logPrefix="Teacher Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-10413">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4" data-id="element-10414">
                <AlertCircle className="w-8 h-8 text-indigo-600" data-id="element-10415" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10416">
                Er ging iets mis
              </h2>
              <p className="text-sm text-slate-500 mb-4 max-w-md" data-id="element-10417">
                {error.message}
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-10418">
                Probeer opnieuw
              </button>
            </div>} data-id="element-10412">
          <PageComponent students={students} selectedGroup={activeGroupLevel} getModuleMastery={getModuleMasteryForClassroom} onSelectStudent={handleSelectStudent} activeAccount={activeAccount} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={setSelectedClassroomId} onNavigate={setActiveRoute} classroomName={activeClassroom?.name} onViewDashboard={handleViewDashboard} initialTab={assessmentInitialTab} initialSearchQuery={assessmentSearchQuery} onViewAssessmentResults={handleViewAssessmentResults} activeTeacherId={activeAccount.id} data-id="element-10419" />
        </BaseErrorBoundary>
      </main>

      {/* Slide-over Panel for Student Details */}
      {insightStudent && <StudentDetailPanel student={insightStudent} selectedGroup={activeGroupLevel} getModuleMastery={getModuleMasteryForClassroom} onClose={handleCloseInsight} onViewDashboard={handleViewDashboard} isOpen={!!insightStudent} classroomName={activeClassroom?.name} onRemoveFromClassroom={isOwner ? handleRemoveFromClassroom : undefined} onUnlinkStudent={isOwner ? handleUnlinkStudent : undefined} onNavigateToAssessments={handleNavigateToAssessments} data-id="element-10420" />}
    </div>;
}